import { GetterTree } from 'vuex';
import { RootState } from '@/store';
import { State } from './state';
import { config } from '@/services/config';
import { forEach, get, map } from 'lodash';
import { TopHeader, Header } from '@/types/Table';
import { formatDataValue } from '@/helpers/format';
import { createTableHeaders, createTopTableHeaders } from '@/helpers/tables';
import {
  ReportingTableData,
  ReportingTableItem,
  ReportingActivity,
  ReportingActivityItem,
} from '@/types/Reporting';

export type Getters = {
  getReportingTable(state: State): ReportingTableData;
  getReportingActivity(state: State, filterKey: string): ReportingActivity;
};

export const getters: GetterTree<State, RootState> & Getters = {
  getReportingTable: state => {
    const tableState = state.table;

    if (tableState) {
      const tableConfig = get(config.layout, 'tableLayouts.reportingTable', {});
      const configTopHeaders = get(tableConfig, 'topHeaders', []) as Array<
        TopHeader
      >;
      const configHeaders = get(tableConfig, 'mainHeaders', []) as Array<
        Header
      >;

      const items = map(
        tableState.reportPer,
        item =>
          ({
            groupName: formatDataValue(item.groupName),
            numRejected: formatDataValue(item.numRejected),
            numImplemented: formatDataValue(item.numImplemented),
            weightedConfidenceLevel: formatDataValue(
              item.weightedConfidenceLevel
            ),
            numOpen: formatDataValue(item.numOpen),
            numUsed: formatDataValue(item.numUsed),
            effectiveProfit: formatDataValue(item.effectiveProfit),
            identifiedProfit: formatDataValue(item.identifiedProfit),
            eOverI: formatDataValue(item.eOverI),
          } as ReportingTableItem)
      );

      const footer = {
        groupName: 'total',
        numRejected: formatDataValue(
          get(tableState, 'reportTotal.numRejected')
        ),
        numImplemented: formatDataValue(
          get(tableState, 'reportTotal.numImplemented')
        ),
        weightedConfidenceLevel: formatDataValue(
          get(tableState, 'reportTotal.weightedConfidenceLevel')
        ),
        numOpen: formatDataValue(get(tableState, 'reportTotal.numOpen')),
        numUsed: formatDataValue(get(tableState, 'reportTotal.numUsed')),
        effectiveProfit: formatDataValue(
          get(tableState, 'reportTotal.effectiveProfit')
        ),
        identifiedProfit: formatDataValue(
          get(tableState, 'reportTotal.identifiedProfit')
        ),
        eOverI: formatDataValue(get(tableState, 'reportTotal.eOverI')),
      } as ReportingTableItem;

      return {
        items: items,
        topHeaders: createTopTableHeaders(configTopHeaders),
        headers: createTableHeaders(configHeaders),
        footer,
        maxProfit: formatDataValue(tableState.maxProfit) as string,
      };
    }
    return {
      items: [] as Array<ReportingTableItem>,
      topHeaders: [] as Array<TopHeader>,
      headers: [] as Array<Header>,
      footer: {} as ReportingTableItem,
      maxProfit: '0',
    } as ReportingTableData;
  },
  getReportingActivity: (state, filterKey) => {
    const activityState = state.activity;

    if (activityState && activityState.years) {
      const activityCategories = get(
        config.layout,
        'reporting.activity-graph.x-axis-categories',
        []
      );

      const formatterType = get(
        config.layout,
        `reporting.activity-graph.filter-format-type.${filterKey}`,
        'number'
      );

      if (activityCategories && activityCategories.length > 0) {
        const reportingActivity = {
          dataKeys: [],
          series: [],
          formatterDataType: formatterType,
        } as ReportingActivity;

        forEach(activityState.years, (item, i) => {
          if (item) {
            reportingActivity.dataKeys.push(
              formatDataValue(item.year) as string
            );
            if (item.quarters && item.quarters.length > 0) {
              reportingActivity.series.push({
                name: formatDataValue(item.year) as string,
                data: map(item.quarters, segment =>
                  get(segment, `${filterKey}.dataValue`, 0)
                ) as Array<number>,
                className: `fill-brand-color-${i + 1}`,
              } as ReportingActivityItem);
            }
          }
        });

        return reportingActivity;
      }
    }

    return {} as ReportingActivity;
  },
};
